import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  Container,
  Heading,
  Stack,
  Text,
  ChakraProvider,
  Button as ChakraButton,
  VStack,
  HStack,
  useDisclosure,
  CircularProgress,
  useToast,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  FormControl,
} from "@chakra-ui/react";

// components
import Header from "components/dashHeader";
import Sidemenu from "components/sidemenu";
import BreadcrumbLink from "components/BreadcrumbLink/BreadcrumbLink";

import Input from "components/Input/Input";
import Select from "components/Select/Select";
import Uploader from "components/Uploader/Uploader";

import UploadSuccessModal from "./Modals/UploadSuccess";

import "pages/styles/dashboard/Account.css";

import ArrowForwardIosRoundedIcon from "@material-ui/icons/ArrowForwardIosRounded";
import { useDispatch, useSelector } from "react-redux";
import {
  clearError,
  resetLevel3Form,
  selectAccountVerification,
  updateLevel3Form,
  uploadLevel3Docs,
} from "store/slices/accountVerification.slice";

import { useEffect, useState, useCallback, useMemo } from "react";
import { uploadFile } from "util/do";
import {
  useAddBankMutation,
  useGetBanksByCountryQuery,
  useGetBanksDetailsQuery,
  useResolveAccountMutation,
} from "services/bankApi";

import { selectProfile } from "store/slices/profile.slice";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { ChevronDownIcon } from "@chakra-ui/icons";

import isEmpty from "lodash/isEmpty";
import sortBy from "lodash/sortBy";
import debounce from "lodash/debounce";

const Button = ({ outlined, children, disabled, ...rest }) => {
  return (
    <ChakraButton
      minW="150px"
      borderRadius="50px"
      color={outlined ? "#03A438" : "white"}
      bg={outlined ? "white" : "#03A438"}
      border={outlined ? "2px solid #03A438" : "none"}
      textTransform="capitalize"
      transition="all 300ms ease-in-out"
      p="10px 20px"
      disabled={disabled}
      _hover={{
        cursor: disabled ? "not-allowed !important" : "pointer",
        boxShadow: "0 0 0 3px #06C3448f, 0 0 10px rgba(0,0,0,.05)",
        transition: "all 300ms ease-in-out",
      }}
      _focus={{
        boxShadow: "0 0 0 3px #06C3448f, 0 0 10px rgba(0,0,0,.05)",
        transition: "all 300ms ease-in-out",
      }}
      _active={{
        boxShadow: "0 0 0 3px #06C3448f, 0 0 10px rgba(0,0,0,.05)",
        transition: "all 300ms ease-in-out",
      }}
      _disabled={{
        opacity: 0.4,
        pointerEvents: "none",
      }}
      {...rest}
    >
      {children}
    </ChakraButton>
  );
};

const BankSelector = ({ disabled, label, bankDetails, onSelect }) => {
  const [state, setState] = useState(null);

  const handleSelect = (data) => {
    setState({ ...data });
    onSelect({ ...data });
  };

  return (
    <Stack>
      {label && (
        <Box
          as="label"
          display="inline-block"
          fontSize=".85em"
          fontWeight="400"
          mb="8px"
          color="#4E4B66"
        >
          {label}
        </Box>
      )}
      <Menu>
        <MenuButton
          minH="56px"
          mt="0 !important"
          borderRadius="16px"
          fontWeight="400"
          textAlign="left"
          pos="relative"
          overflow="hidden"
          as={ChakraButton}
          rightIcon={<ChevronDownIcon />}
          _hover={{
            // cursor: disabled ? "not-allowed !important" : "pointer",
            boxShadow: "0 0 0 3px #06C3448f, 0 0 10px rgba(0,0,0,.05)",
            transition: "all 300ms ease-in-out",
          }}
          _focus={{
            boxShadow: "0 0 0 3px #06C3448f, 0 0 10px rgba(0,0,0,.05)",
            transition: "all 300ms ease-in-out",
          }}
          _active={{
            boxShadow: "0 0 0 3px #06C3448f, 0 0 10px rgba(0,0,0,.05)",
            transition: "all 300ms ease-in-out",
          }}
          _disabled={{
            opacity: 0.4,
            pointerEvents: "none",
          }}
        >
          <Text maxW="100%" overflow="hidden">
            {!state
              ? "Select Bank"
              : `${state?.bankName ?? ""} - ${state?.bankAccountNumber ?? ""}`}
          </Text>
        </MenuButton>
        <MenuList p="0" overflow="hidden" w="100">
          {bankDetails.map((bank) => (
            <MenuItem
              _hover={{
                bg: "#06c34469",
              }}
              onClick={() =>
                handleSelect({ ...bank, branchName: null, bankId: bank._id })
              }
            >
              <Stack>
                <Text m="2px 0 !important" fontWeight="500">
                  {bank?.bankName ?? ""}
                </Text>
                <HStack m="0 !important" gridGap="20px" fontSize="xs">
                  <Text m="0 !important">{bank?.bankAccountName ?? ""}</Text>
                  <span>-</span>
                  <Text m="0 !important">{bank?.bankAccountNumber ?? ""}</Text>
                </HStack>
              </Stack>
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
    </Stack>
  );
};

const BankVerification = ({
  isLoadingBankData,
  isBankDetailsLoading,
  country,
  banks,
  bankDetails,
  state,
  setState,
}) => {
  // console.log("BANK DETAILS", bankDetails, banks);

  // const bankDetails = [];

  const [
    resolveBank,
    { data: resolvedBank, isLoading: isResolvingBank /*error, isError*/ },
  ] = useResolveAccountMutation();

  // console.log("RESOLVED BANK", resolvedBank);

  // eslint-disable-next-line
  const debouncedBankResolver = useCallback(
    (bankAccountNumber) =>
      debounce(
        () =>
          state?.bankCode &&
          resolveBank({
            bankCode: state.bankCode,
            bankAccountNumber,
          }),
        2500
      ),

    [state?.bankCode, resolveBank]
  );

  const bankAccountName = useMemo(
    () => resolvedBank?.account_name,
    [resolvedBank]
  );

  useEffect(() => {
    if (resolvedBank)
      setState({
        bankAccountName: resolvedBank?.account_name,
      });

    // eslint-disable-next-line
  }, [resolvedBank]);

  const handleAccountNumber = (e) => {
    setState({ bankAccountNumber: e.target.value });

    // console.log("account number", e.target.value);

    if (e.target.value.length >= 7) {
      country === "nigeria" && debouncedBankResolver(e.target.value)();
    }
  };

  const handleBankName = (e) => {
    const [bankCode, bankName] = String(e.target.value).split("-");
    setState({ bankCode, bankName });
  };

  const handleBank = (data) => {
    setState({ ...data });
  };

  return (
    <Box>
      <HStack color="#4E4B66">
        <Heading fontSize="1em" fontWeight="500">
          Bank Verification
        </Heading>

        {(isBankDetailsLoading || isLoadingBankData || isResolvingBank) && (
          <CircularProgress size="16px" isIndeterminate color="green.300" />
        )}
      </HStack>

      <Box as="form">
        <Container maxW={["100%", "100%", "20em"]} m="0" p="0">
          <Stack mt="30px">
            {isEmpty(bankDetails) && !isBankDetailsLoading && (
              <Select
                isLoading={isLoadingBankData}
                value={`${state?.bankCode ?? ""}-${state?.bankName ?? ""}`}
                onChange={handleBankName}
                label="Bank Name"
                placeholder="Choose bank"
              >
                {banks &&
                  banks.map((bank, i) => (
                    <option key={i} value={`${bank.bankCode}-${bank.bankName}`}>
                      {bank.bankName}
                    </option>
                  ))}
              </Select>
            )}
            {/* {bankDetails && !isLoading && (
              <Select
                isLoading={isLoadingBankData}
                onChange={handleBank}
                label="Bank"
                placeholder="Choose bank"
              >
                {bankDetails.map((bank) => (
                  <option value={`${bank._id}-${bank.bankAccountName}`}>
                    {bank.bankName} - {bank.bankAccountNumber}
                  </option>
                ))}
              </Select>
            )} */}
            {!isEmpty(bankDetails) && !isBankDetailsLoading && (
              <BankSelector
                label="Bank"
                bankDetails={bankDetails}
                onSelect={handleBank}
              />
            )}
            {country &&
              String(country).toLowerCase() !== "nigeria" &&
              !isBankDetailsLoading && (
                <Input
                  value={state?.branchName ?? ""}
                  onChange={(e) => setState({ branchName: e.target.value })}
                  label="Branch"
                  placeholder="Enter branch name"
                />
              )}

            {isEmpty(bankDetails) && !isBankDetailsLoading && (
              <Input
                value={state?.bankAccountNumber ?? ""}
                onChange={handleAccountNumber}
                label="Bank Account No."
                placeholder="Enter account no."
              />
            )}

            {(!isEmpty(bankDetails) ||
              (country && String(country).toLowerCase() !== "nigeria")) &&
              !isBankDetailsLoading && (
                <Input
                  disabled={!isEmpty(bankDetails)}
                  value={state?.bankAccountName ?? ""}
                  onChange={(e) =>
                    setState({ bankAccountName: e.target.value })
                  }
                  label="Bank Account Name"
                  placeholder="Enter bank account name"
                />
              )}

            {isEmpty(bankDetails) &&
              country &&
              String(country).toLowerCase() === "nigeria" &&
              !state?.bankId &&
              !isBankDetailsLoading && (
                <Input
                  disabled
                  value={bankAccountName ?? ""}
                  onChange={undefined}
                  label="Bank Account Name"
                />
              )}
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

const AddressVerification = ({ country, onFileChange, state, setState }) => {
  return (
    <Box mt="28px">
      <Stack color="#4E4B66">
        <Heading fontSize="1em" fontWeight="500">
          Address Verification
        </Heading>
      </Stack>

      <Stack gridGap="12px" mt="20px">
        <Container maxW={["100%", "100%", "20em"]} m="0" p="0">
          <FormControl>
            <Input
              label="Street"
              placeholder="Enter e.g Adeola Odeku"
              // pattern="([a-zA-Z])\w+"
              value={state?.street ?? ""}
              onChange={(e) => setState({ street: e.target.value })}
            />
          </FormControl>
          <FormControl mt="20px !important">
            <Input
              label="Flat, suite, unit, building or floor number"
              placeholder="Enter e.g 40 Christopher Close"
              value={state?.streetNumber ?? ""}
              onChange={(e) =>
                setState({
                  streetNumber: e.target.value,
                  address: e.target.value,
                })
              }
            />
          </FormControl>
          <FormControl mt="20px !important">
            <Input
              label="Town/City"
              placeholder="Enter city e.g Lekki"
              // pattern="([a-zA-Z])\w+"
              value={state?.city ?? ""}
              onChange={(e) => setState({ city: e.target.value })}
            />
          </FormControl>
          <FormControl mt="20px !important">
            <Input
              label="State/District"
              placeholder="Enter state e.g Lagos"
              // pattern="([a-zA-Z])\w+"
              value={state?.state ?? ""}
              onChange={(e) => setState({ state: e.target.value })}
            />
          </FormControl>

          <FormControl mt="20px !important">
            <Input
              label="Zip Code"
              placeholder="Enter zipcode"
              value={state?.zipCode ?? ""}
              onChange={(e) => setState({ zipCode: e.target.value })}
            />
          </FormControl>
        </Container>

        {country !== "kenya" && (
          <Box>
            <Text fontSize="1rem" fontWeight="500">
              Disclaimer: Documents will only be used to confirm your residence.
            </Text>
            <Text fontSize=".9em" fontWeight="400">
              Please upload a utility bill (electricity, waste or water) or a
              bank statement.
            </Text>
            <VStack
              justify="center"
              w="100%"
              p={["26px 0", "26px 0", "26px"]}
              pt="5px"
              gridGap="12px"
            >
              <Uploader onFiles={(files) => onFileChange(files[0])} />
              <Text m="0 !important" color="#4E4B66" fontSize=".8em">
                Upload .PDF/.JPG/.JPEG/.PNG file and no more than 5M
              </Text>
            </VStack>
          </Box>
        )}
      </Stack>
    </Box>
  );
};

const BillsInfo = ({ onFileChange, state, setState }) => {
  return (
    <Box mt="28px">
      <Stack color="#4E4B66">
        <Heading fontSize="1em" fontWeight="500">
          Bank Information
        </Heading>

        <Text fontSize="sm" color="grey.300">
          Please select a financial statement to upload to verify your
          verification
        </Text>
      </Stack>

      <Stack gridGap="12px" mt="20px">
        <Container maxW={["100%", "100%", "20em"]} m="0" p="0">
          <FormControl>
            <Select
              label="Select Document Type"
              placeholder="Select document type"
              value={state?.docType ?? ""}
              onChange={(e) => setState({ docType: e.target.value })}
            >
              <option value="mpesa_statement">MPESA Statement</option>
              <option value="postpaid_bill">Postpaid Bill</option>
              <option value="internet_bill">Internet Bill</option>
            </Select>
          </FormControl>
        </Container>

        <VStack
          justify="center"
          w="100%"
          // p={["26px 0", "26px 0", "26px"]}
          pt="5px"
          gridGap="12px"
        >
          <Uploader onFiles={(files) => onFileChange(files[0])} />
          <Text m="0 !important" color="#4E4B66" fontSize=".8em">
            Upload .PDF/.JPG/.JPEG/.PNG file and no more than 5M
          </Text>
        </VStack>
      </Stack>
    </Box>
  );
};

const AccountLevel3Verificaition = (props) => {
  const userData = props.userData;
  const toast = useToast();
  const history = useHistory();
  const dispatch = useDispatch();
  const {
    profile: { country: profileCountry },
  } = useSelector(selectProfile);
  const {
    error,
    level3: { status },
  } = useSelector(selectAccountVerification);

  const country =
    (profileCountry && String(profileCountry).toLowerCase()) ??
    (userData?.country && String(userData?.country).toLowerCase());

  const countryCodeMap = {
    nigeria: "ng",
    ghana: "gh",
    kenya: "ke",
  };

  const [
    addBank,
    {
      data: addBankData,
      isLoading: isAddingBank,
      error: addBankError,
      isError: addBankHasError,
      // isSuccess: isAddBankSuccess,
    },
  ] = useAddBankMutation();

  console.log("BANK INFO", addBankData);

  const { data, isLoading } = useGetBanksByCountryQuery(
    countryCodeMap[String(country).toLowerCase()],
    { skip: !country }
  );
  const {
    data: bankDetails,
    isLoading: bankDetailsLoading,
    // error: bankDetailsError,
  } = useGetBanksDetailsQuery();

  const bankDetailsData = bankDetails?.filter(
    (bank) => bank.country === String(country).toLowerCase()
  );

  // console.log("BANK DETAILS DATA", bankDetailsData);

  const getBankList = (data) => {
    const sort = (list) =>
      sortBy(list, ({ bankName }) => bankName?.toLowerCase());
    if (country === "nigeria" && data) return sort(data["bankPayloads"]);
    if (country === "ghana" && data) return sort(data["ghanaBankPayloads"]);
  };

  const { isOpen, onClose, onOpen } = useDisclosure();
  const [isUploading, setIsUploading] = useState(false);

  const [file, setFile] = useState();
  const [state, setState] = useState({
    bankId: null,
    branchName: null,
    bankName: null,
    bankCode: null,
    bankAccountName: null,
    bankAccountNumber: null,
    address: null,
    street: null,
    city: null,
    zipCode: null,
    bankStatementLink: null,
    state: null,
  });

  const handleFormState = (newState) => {
    setState({ ...state, ...newState });
  };

  console.log("FORM STATE DATA", state);

  const handleUpload = async () => {
    if (!file) return;
    setIsUploading(true);
    const filename = `idDoc_level3_primary_document_${userData?.userId}`;
    let bankId = "";
    let location = "";

    if (!state?.bankId && country !== "kenya") {
      const toProcess = [
        uploadFile(file, filename),
        addBank({
          ...state,
          address: state?.street ?? state?.address ?? "",
          country: countryCodeMap[country],
        }),
      ];

      const res = await Promise.all(toProcess);
      location = res[0].location;
      bankId = res[1]?.data?.data?.newBankDetail?._id;

      // console.log(
      //   "ADD BANK RESULT",
      //   res[0]?.location,
      //   res[1]?.data?.data?.newBankDetail?._id
      // );
    } else {
      const res = await uploadFile(file, filename);
      location = res?.location;
      bankId = state?.bankId;
    }

    const reqData = {};
    if (country !== "kenya")
      Object.assign(reqData, {
        ...state,
        bankId: state?.bankId ?? bankId,
        link: location,
        bankStatementLink: location,
        country: countryCodeMap[String(country).toLowerCase()],
      });

    if (country === "kenya")
      Object.assign(reqData, {
        ...state,
        link: location,
        useBankId: true,
        bankStatementLink: location,
        country: countryCodeMap[String(country).toLowerCase()],
      });

    // console.log("UPLOADED DOC", res);
    dispatch(updateLevel3Form(reqData));
    dispatch(uploadLevel3Docs());
    setIsUploading(false);
  };

  useEffect(() => {
    if (error.message || (addBankHasError && addBankError)) {
      toast({
        position: "bottom-right",
        title: "Oops an error occurred",
        description: error?.message ?? addBankError?.data?.error,
        status: "error",
        duration: 9000,
        isClosable: true,
        onCloseComplete: () => {
          dispatch(clearError());
        },
      });
    }
  }, [error?.message, dispatch, toast, addBankHasError, addBankError]);

  useEffect(() => {
    if (status === "success") {
      onOpen();
    }
  }, [status, onOpen]);

  const isDisabled = () =>
    !(
      file &&
      state.city &&
      state.state &&
      state.street &&
      state.zipCode &&
      state.address
    ) ||
    (country !== "kenya" &&
      !(state.bankName && state.bankCode && state.bankAccountNumber)) ||
    isUploading ||
    isAddingBank ||
    status === "loading" ||
    (country === "kenya" && !state?.docType && !file);
  // console.log(state);
  return (
    <Box>
      <ChakraProvider resetCSS>
        <Sidemenu currentPage="Account" logoutUser={props.logout} />
        <Header />

        <Box as="section">
          <Box
            className="account-container"
            p={["6em 0 !important", "6.5em 1.5em !important"]}
          >
            <Container
              maxW={["100%", "100%", "calc(100% - 15vw)", "calc(100% - 30vw)"]}
              m="0"
              p={[0, 0, "1rem"]}
            >
              <Stack mx={["24px", 0]}>
                <Heading>Verification</Heading>
                <Breadcrumb
                  className="animate__animated animate__fadeInDown"
                  separator={
                    <ArrowForwardIosRoundedIcon
                      style={{ fontSize: ".8em", color: "#6E7191" }}
                    />
                  }
                >
                  <BreadcrumbItem>
                    <BreadcrumbLink to="/dashboard/account">
                      Account
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbItem>
                    <BreadcrumbLink to="/dashboard/account/verification">
                      Verification
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbItem isCurrentPage>
                    <BreadcrumbLink isCurrentPage to="">
                      Level 3
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                </Breadcrumb>
              </Stack>

              <Box
                bg="white"
                p={["32px 24px", "32px 24px", "45px 40px"]}
                mt={["24px", "50px"]}
                borderRadius={[0, "8px", "16px"]}
              >
                <Stack gridGap="10px">
                  <Heading color="#14142B" fontSize="1.3em" fontWeight="600">
                    Level 3 Verification
                  </Heading>
                  <Text color="#4E4B66" fontSize=".9em" m="0 !important">
                    Please provide the information required to continue with
                    your account upgrade.
                  </Text>
                </Stack>

                <Box mt="50px" mb={["50px", 0]}>
                  {country !== "kenya" && (
                    <BankVerification
                      country={country}
                      isLoadingBankData={isLoading}
                      isBankDetailsLoading={bankDetailsLoading}
                      banks={getBankList(data)}
                      bankDetails={bankDetailsData}
                      state={state}
                      setState={handleFormState}
                    />
                  )}
                  <AddressVerification
                    state={state}
                    country={country}
                    setState={handleFormState}
                    onFileChange={(file) => setFile(file)}
                  />

                  {country === "kenya" && (
                    <BillsInfo
                      state={state}
                      country={country}
                      setState={handleFormState}
                      onFileChange={(file) => setFile(file)}
                    />
                  )}
                </Box>

                <HStack w="100%" justify="center" mt="60px" gridGap="20px">
                  <Button
                    disabled={
                      isUploading || isAddingBank || status === "loading"
                    }
                    onClick={() => history.goBack()}
                    outlined
                  >
                    Back
                  </Button>
                  <Button
                    disabled={isDisabled()}
                    onClick={() => {
                      handleUpload();
                    }}
                  >
                    {(isUploading || isAddingBank || status === "loading") && (
                      <CircularProgress
                        size="18px"
                        isIndeterminate
                        color="green.300"
                      />
                    )}
                    {!isUploading &&
                      !isAddingBank &&
                      status !== "loading" &&
                      "Upload"}
                  </Button>
                </HStack>
              </Box>

              <UploadSuccessModal
                isOpen={isOpen}
                onClose={() => {
                  onClose();
                  dispatch(resetLevel3Form());
                  history.replace("/dashboard/account/verification");
                }}
              />
            </Container>
          </Box>
        </Box>
      </ChakraProvider>
    </Box>
  );
};

export default AccountLevel3Verificaition;
